import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
} from "recharts";

// Demo data for visualization - in production this would come from URL params / API
const demoTreatmentData = [
  { name: "Surgery\nOnly", survival: 62, color: "#1e40af" },
  { name: "+ Hormone\nTherapy", survival: 74, color: "#2563eb" },
  { name: "+ Chemo-\ntherapy", survival: 79, color: "#3b82f6" },
  { name: "+ Trastu-\nzumab", survival: 83, color: "#60a5fa" },
  { name: "+ Bisphos-\nphonates", survival: 85, color: "#93c5fd" },
];

const demoKpis = [
  {
    label: "5-Year Survival",
    value: "79%",
    desc: "With recommended treatment",
  },
  {
    label: "10-Year Survival",
    value: "62%",
    desc: "With recommended treatment",
  },
  {
    label: "Hormone Benefit",
    value: "+12%",
    desc: "Additional survival benefit",
  },
  {
    label: "Chemo Benefit",
    value: "+5%",
    desc: "Additional from chemotherapy",
  },
];

const insights = [
  {
    title: "Prognosis",
    body: "Your survival probability improves significantly with hormone therapy. Endocrine therapy is strongly recommended based on ER-positive status.",
    recommendation:
      "Consider 5-10 years of endocrine therapy to maximize survival benefit.",
  },
  {
    title: "Treatment Benefit",
    body: "Chemotherapy adds an additional 5% survival benefit beyond hormone therapy. The benefit should be weighed against side effects.",
    recommendation:
      "Discuss chemotherapy with your oncologist, particularly if high-risk features are present.",
  },
  {
    title: "Key Factors",
    body: "Tumor grade, lymph node status, and receptor profile are the main factors influencing this prediction.",
    recommendation:
      "Regular follow-up and adherence to the treatment plan are essential.",
  },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-lg font-bold text-blue-700">{payload[0].value}%</p>
        <p className="text-xs text-slate-400">Overall survival</p>
      </div>
    );
  }
  return null;
};

const ResultsPage: React.FC = () => {
  const [activeBar, setActiveBar] = useState<number | null>(null);

  return (
    <>
      <Helmet>
        <title>Prediction Results - Boobalyz</title>
        <meta
          name="description"
          content="Breast cancer risk prediction results and treatment recommendations"
        />
      </Helmet>

      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-2xl font-bold text-slate-900">
                Prediction Results
              </h1>
              <div className="flex gap-3">
                <Link
                  to="/assessment"
                  className="px-4 py-2 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Edit Assessment
                </Link>
                <button className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                  Download Report
                </button>
              </div>
            </div>
            <p className="text-slate-500 text-sm">
              AI has generated your breast cancer risk analysis based on patient
              data
            </p>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {demoKpis.map((kpi, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-slate-200 p-4"
              >
                <div className="text-2xl font-bold text-slate-900">
                  {kpi.value}
                </div>
                <div className="text-sm font-medium text-slate-700 mt-0.5">
                  {kpi.label}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">{kpi.desc}</div>
              </div>
            ))}
          </div>

          {/* Main content: Charts + Insights */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Charts column (2/3 width) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Treatment Benefit Bar Chart */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-base font-semibold text-slate-900">
                      Treatment Benefit Analysis
                    </h2>
                    <p className="text-xs text-slate-400 mt-0.5">
                      10-year overall survival by treatment option
                    </p>
                  </div>
                  <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 rounded px-2 py-1">
                    ⓘ
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={demoTreatmentData}
                    margin={{ top: 5, right: 10, left: 0, bottom: 20 }}
                    onMouseLeave={() => setActiveBar(null)}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      axisLine={{ stroke: "#e2e8f0" }}
                      tickLine={false}
                      interval={0}
                    />
                    <YAxis
                      domain={[0, 100]}
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={false} />
                    <Bar
                      dataKey="survival"
                      radius={[4, 4, 0, 0]}
                      onMouseEnter={(_, index) => setActiveBar(index)}
                    >
                      {demoTreatmentData.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={activeBar === index ? "#1e40af" : entry.color}
                          opacity={
                            activeBar !== null && activeBar !== index ? 0.6 : 1
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {/* Legend */}
                <div className="flex items-center gap-4 mt-2 justify-center">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-blue-600" />
                    <span className="text-xs text-slate-500">Survival %</span>
                  </div>
                </div>
              </div>

              {/* Treatment Summary Table */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h2 className="text-base font-semibold text-slate-900 mb-4">
                  Treatment Summary
                </h2>
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-blue-600 text-white">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">
                          Treatment
                        </th>
                        <th className="px-4 py-3 text-center font-semibold">
                          Benefit
                        </th>
                        <th className="px-4 py-3 text-center font-semibold">
                          10-yr Survival
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {demoTreatmentData.map((t, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-4 py-2.5 font-medium text-slate-700">
                            {t.name.replace(/\n/g, " ")}
                          </td>
                          <td className="px-4 py-2.5 text-center text-slate-500">
                            {i === 0
                              ? "—"
                              : `+${t.survival - demoTreatmentData[0].survival}%`}
                          </td>
                          <td className="px-4 py-2.5 text-center font-bold text-blue-700">
                            {t.survival}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Insights column (1/3 width) — blue cards matching XPlan */}
            <div className="space-y-4">
              {insights.map((insight, i) => (
                <div key={i} className="bg-blue-700 rounded-xl p-5 text-white">
                  <h3 className="font-bold text-base mb-2">{insight.title}</h3>
                  <p className="text-blue-100 text-sm leading-relaxed mb-3">
                    {insight.body}
                  </p>
                  <div className="bg-blue-600 rounded-lg p-3">
                    <p className="text-blue-100 text-xs leading-relaxed">
                      <strong>Recommendation:</strong> {insight.recommendation}
                    </p>
                  </div>
                </div>
              ))}

              {/* Quick Actions */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-900 text-sm mb-3">
                  Quick Actions
                </h3>
                <div className="space-y-2">
                  <Link
                    to="/assessment"
                    className="block w-full text-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    New Assessment
                  </Link>
                  <Link
                    to="/history"
                    className="block w-full text-center px-3 py-2 border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    View History
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResultsPage;
